import { useEffect, useMemo } from 'react';

import { useSink } from './useSink';

import { SINK_OPTIONS, SortBy } from '~/core/enums';
import { ApplianceModel, FilterOption } from '~/core/models';
import {
  FilterInput,
  SelectedFilterOptions,
  TypeOfFilter,
} from '~/core/types/sortAndFilter';
import {
  convertFiltersModelToUI,
  convertFiltersUIToModel,
  filterAllEntries,
  getSelectedFiltersMap,
  getSelectedFiltersOptions,
  initializeFiltersOptions,
  sortAppliancesBy,
  updateListAndFilters,
} from '~/core/utils/sortAndFilter';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectSelectedBrowsingOption } from '~/redux/slices/sink';
import {
  selectFilters,
  selectSelectedFilters,
  selectSortBy,
  updateFilters,
} from '~/redux/slices/sortAndFilter';

type UseFilteredSinkReturn = {
  sortBy: SortBy;
  sinkFilters: TypeOfFilter[];
  filteredAndSortedSinks: ApplianceModel[];
  selectedFilters: SelectedFilterOptions;
  handleFiltersChange: (options: SelectedFilterOptions) => void;
};

export const useFilteredSink = (): UseFilteredSinkReturn => {
  const dispatch = useAppDispatch();

  const { customerSuppliedSinks, browsedSinks } = useSink();

  const sortBy = useAppSelector(selectSortBy('sink'));
  const _sinkFilters = useAppSelector(selectFilters('sink'));
  const selectedFilters = useAppSelector(selectSelectedFilters('sink'));
  const selectedSinkOption = useAppSelector(selectSelectedBrowsingOption);

  const initialFilterOptions: FilterInput = useMemo(() => {
    let initialFilters: FilterInput = {};
    const setSinkFilters = (val: FilterInput) => {
      initialFilters = val;
    };

    if (browsedSinks.length > 0) {
      initializeFiltersOptions(browsedSinks, setSinkFilters);
    }

    return initialFilters;
  }, [browsedSinks]);

  const convertedInitialFilterOptions = useMemo(
    () => convertFiltersModelToUI(initialFilterOptions),
    [initialFilterOptions]
  );

  const sinkFilters = useMemo(() => {
    const isNotEmptyObject =
      _sinkFilters && Object.keys(_sinkFilters).length > 0;

    return isNotEmptyObject ? _sinkFilters : convertedInitialFilterOptions;
  }, [_sinkFilters, convertedInitialFilterOptions]);

  const modelFilters = useMemo(
    () => convertFiltersUIToModel(sinkFilters, selectedFilters),
    [sinkFilters, selectedFilters]
  );

  const filteredSinks = useMemo(() => {
    const selectedSinkFilters: Record<string, FilterOption[]> =
      getSelectedFiltersMap(modelFilters);

    const entries = Object.entries(selectedSinkFilters);

    return filterAllEntries(entries, browsedSinks);
  }, [modelFilters, browsedSinks]);

  const filteredAndSortedSinks = useMemo(() => {
    const sinksByOption: Record<SINK_OPTIONS, ApplianceModel[]> = {
      [SINK_OPTIONS.BROWSE_SINKS]: filteredSinks,
      [SINK_OPTIONS.SUPPLY_SINKS]: customerSuppliedSinks,
      [SINK_OPTIONS.NO_SINKS]: [],
    };
    const sinksFilteredBySelection =
      sinksByOption[selectedSinkOption ?? SINK_OPTIONS.NO_SINKS];

    return sortAppliancesBy[sortBy](sinksFilteredBySelection);
  }, [customerSuppliedSinks, filteredSinks, selectedSinkOption, sortBy]);

  const handleFiltersChange = (options: SelectedFilterOptions) => {
    const { filters } = updateListAndFilters(browsedSinks, options);

    dispatch(
      updateFilters({
        component: 'sink',
        filters,
        selectedFilters: options,
      })
    );
  };

  useEffect(() => {
    const { filters } = updateListAndFilters(browsedSinks, selectedFilters);

    dispatch(
      updateFilters({
        component: 'sink',
        filters,
        selectedFilters:
          selectedFilters || getSelectedFiltersOptions(sinkFilters),
      })
    );
  }, [dispatch, sinkFilters, selectedFilters, browsedSinks]);

  return {
    sortBy,
    sinkFilters,
    filteredAndSortedSinks,
    handleFiltersChange,
    selectedFilters,
  };
};
